import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type VerifyStatus = 'loading' | 'success' | 'error';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerifyStatus>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const handleVerification = async () => {
      // Supabase 驗證信的 URL 格式有兩種：
      // 1. 舊版：hash fragment  #access_token=...&type=signup
      // 2. 新版：query string  ?token_hash=...&type=signup
      const hash = window.location.hash.substring(1);
      const query = window.location.search.substring(1);
      const hashParams = new URLSearchParams(hash);
      const queryParams = new URLSearchParams(query);

      const type = hashParams.get('type') || queryParams.get('type');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const tokenHash = queryParams.get('token_hash');

      if (type === 'signup' || type === 'email') {
        try {
          if (tokenHash) {
            // 新版 OTP 驗證
            const { error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: 'signup',
            });
            if (error) {
              setErrorMsg(error.message);
              setStatus('error');
            } else {
              setStatus('success');
            }
          } else if (accessToken && refreshToken) {
            // 舊版 session 設定
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (error) {
              setErrorMsg(error.message);
              setStatus('error');
            } else {
              setStatus('success');
            }
          } else {
            setErrorMsg('驗證連結格式不正確，請重新申請驗證信。');
            setStatus('error');
          }
        } catch {
          setErrorMsg('驗證過程發生錯誤，請稍後再試。');
          setStatus('error');
        }
      } else if (type === 'recovery') {
        // 重設密碼連結誤導到此頁，轉向正確頁面
        navigate('/reset-password' + window.location.hash, { replace: true });
      } else {
        setErrorMsg('無效的驗證連結或連結已過期，請重新申請驗證信。');
        setStatus('error');
      }
    };

    handleVerification();
  }, [navigate]);

  // 載入中
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <i className="ri-mail-check-line text-4xl text-orange-500"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">驗證中...</h2>
          <p className="text-gray-500 flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin text-orange-500"></i>
            正在確認您的 Email，請稍候
          </p>
        </div>
      </div>
    );
  }

  // 驗證成功
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* 成功動畫圖示 */}
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-mail-check-line text-5xl text-green-500"></i>
              </div>
              <div className="absolute -bottom-1 -right-1 w-9 h-9 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                <i className="ri-check-line text-white text-lg"></i>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">Email 驗證成功！</h2>
            <p className="text-gray-600 mb-2">您的帳號已完成驗證，歡迎加入！</p>
            <p className="text-sm text-gray-400 mb-8">現在可以登入並開始使用所有功能</p>

            {/* 功能提示 */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 text-left">
              <p className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                <i className="ri-gift-line"></i>
                驗證完成後您可以：
              </p>
              <ul className="space-y-1.5 text-sm text-green-700">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-500"></i>
                  購買遊戲點數與道具
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-500"></i>
                  查看訂單記錄與狀態
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-500"></i>
                  使用優惠券與推薦獎勵
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-500"></i>
                  管理錢包與儲值紀錄
                </li>
              </ul>
            </div>

            <button
              onClick={() => navigate('/account')}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-login-box-line"></i>
              立即登入
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 驗證失敗
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-mail-close-line text-4xl text-red-500"></i>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-3">驗證失敗</h2>
          <p className="text-gray-600 mb-2">Email 驗證連結無效或已過期</p>
          {errorMsg && (
            <p className="text-sm text-red-500 mb-6 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              {errorMsg}
            </p>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 text-left">
            <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <i className="ri-information-line"></i>
              可能的原因：
            </p>
            <ul className="space-y-1.5 text-sm text-amber-700">
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line mt-0.5 flex-shrink-0"></i>
                驗證連結已超過 24 小時有效期
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line mt-0.5 flex-shrink-0"></i>
                連結已被使用過（每個連結只能用一次）
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line mt-0.5 flex-shrink-0"></i>
                連結在複製時不完整
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/account')}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-orange-600 hover:to-amber-600 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line"></i>
              前往登入並重新發送驗證信
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首頁
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
